import { styled } from "styled-components";
import { Colors } from "../constants";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import Matter from "matter-js";
import DieImage from "../img/Die.png";

const Wrapper = styled(motion.div)`
  color: ${Colors.Tan};
  text-align: center;
  position: relative;
  flex: 1;
  font-family: "UnifrakturMaguntia", cursive;
  font-size: 80px;
  line-height: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 1024px;
  @media (max-width: 800px) {
    font-size: 48px;
    line-height: 100%;
    height: auto;
    flex: none;
    height: 256px;
  }
`;

const Die = styled(motion.div)`
  position: absolute;
  background: url(${DieImage}) no-repeat;
  background-size: 100%;
  height: 118px;
  width: 102px;
`;

const RollWrapper = styled(motion.div)`
  width: 102px;
  height: 118px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "UnifrakturCook", cursive;
  font-weight: bold;
  font-size: 64px;
  line-height: 100%;
  color: ${Colors.Tan};
  text-shadow: 0 -2px 0 rgba(0, 0, 0, 0.2);
`;

const RollName = styled(motion.div)``;

const RollTitle = styled.span`
  text-transform: capitalize;
`;

const RollValue = styled(motion.div)`
  font-family: "UnifrakturCook", cursive;
  display: inline-block;
`;

const DefenseRollMessage = ({ character, roll }) => {
  return (
    <>
      {roll.value > 10 + character.bonuses.defense ? (
        <>
          <RollValue>{roll.withBonus}</RollValue>
          <RollTitle>. You were killed.</RollTitle>
        </>
      ) : (
        <>
          <RollValue>{roll.withBonus}</RollValue>
          <RollTitle>. You survived.</RollTitle>
        </>
      )}
    </>
  );
};

const DieRoll = ({ character, roll }) => {
  const boxRef = useRef(null);
  const requestRef = useRef();
  const engineRef = useRef();
  const runnerRef = useRef();
  const containerRef = useRef(null);
  let box;

  const animate = () => {
    engineRef.current = Matter.Engine.create();
    runnerRef.current = Matter.Runner.create();
    const engine = engineRef.current;
    const containerRect = containerRef.current.getBoundingClientRect();
    box = {
      body: Matter.Bodies.polygon(
        Math.random() * containerRect.width,
        0,
        6,
        59,
        {
          frictionAir: 0.021,
          frictionStatic: 0.021,
          restitution: 0.4,
        }
      ),
      elem: boxRef.current,
      render() {
        if (this.elem) {
          const { x, y } = this.body.position;
          this.elem.style.top = `${y - 59}px`;
          this.elem.style.left = `${x - 59}px`;
          this.elem.style.transform = `rotate(${this.body.angle}rad)`;
        }
      },
    };

    Matter.Body.applyForce(box.body, box.body.position, { x: 0, y: 0.5 });
    Matter.Body.setAngularVelocity(box.body, Math.random() > 0.5 ? 0.3 : -0.3);
    Matter.Composite.add(engine.world, [
      box.body,
      Matter.Bodies.rectangle(
        containerRect.width / 2, // x
        containerRect.height + 50, // y
        containerRect.width, // w
        100, // h
        { isStatic: true }
      ),
      Matter.Bodies.rectangle(
        containerRect.width + 50,
        containerRect.height / 2,
        100,
        containerRect.height,
        {
          isStatic: true,
        }
      ),
      Matter.Bodies.rectangle(
        -50,
        containerRect.height / 2,
        100,
        containerRect.height,
        {
          isStatic: true,
        }
      ),
    ]);

    (function rerender() {
      box.render();
      Matter.Engine.update(engine);
      requestRef.current = requestAnimationFrame(rerender);
    })();
  };

  useEffect(() => {
    animate();
    return () => {
      cancelAnimationFrame(requestRef.current);
      Matter.Engine.clear(engineRef.current);
      Matter.Events.off(engineRef.current, "afterUpdate");
    };
  }, []);

  return (
    <Wrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: "ease-out", duration: 0.25 }}
      ref={containerRef}
    >
      <RollName
        initial={{ translateY: -10 }}
        animate={{ translateY: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 10 }}
      >
        {roll.name === "" ? (
          <>
            You rolled <RollValue>{roll.withBonus}</RollValue>
          </>
        ) : roll.name === "defense" && character ? (
          <DefenseRollMessage character={character} roll={roll} />
        ) : (
          <>
            <RollTitle>{roll.name}</RollTitle> for{" "}
            <RollValue>{roll.withBonus}</RollValue>
          </>
        )}
      </RollName>
      <Die ref={boxRef}>
        <RollWrapper>{roll.withBonus}</RollWrapper>
      </Die>
    </Wrapper>
  );
};

export default DieRoll;
